/* ===========================================================
   Conteúdo do GLOSSÁRIO (spokes do cluster "lucro real no e-commerce").
   Dados puros, sem JSX — a tela (screens/conteudo/glossario.tsx) renderiza.
   Fonte da verdade conceitual: public/llms.txt (mesmas definições, sem se
   contradizer). Cada verbete é uma URL indexável /glossario/<slug>.

   ⚠ Os slugs aqui precisam casar com as rotas em app/routes.tsx e com a
   lista em scripts/rotas-publicas.mjs (prerender + sitemap).
   =========================================================== */

export interface SecaoConteudo {
  h: string;
  /** Parágrafos do corpo. HTML simples (ex.: <strong>) é permitido — render
   *  controlado, sem input do usuário. */
  p: string[];
}

export interface Verbete {
  slug: string;
  /** Termo principal (vira o <h1>). */
  termo: string;
  /** Título-pergunta usado como h1 do post e no card do blog ("O que é X?"). */
  pergunta: string;
  /** Como aparece no <title>: "ROAS de equilíbrio: o que é e como calcular". */
  tituloSeo: string;
  /** 1–2 frases — meta description, definição do DefinedTerm e abertura. */
  resumo: string;
  secoes: ReadonlyArray<SecaoConteudo>;
  /** Slugs de verbetes relacionados (links internos do cluster). */
  relacionados: ReadonlyArray<string>;
  /** Calculadora ligada a este conceito, se houver (path de rota). */
  calculadora?: { rotulo: string; path: string };
}

export const VERBETES: ReadonlyArray<Verbete> = [
  {
    slug: "roas",
    termo: "ROAS",
    pergunta: "O que é ROAS e por que ele não é lucro?",
    tituloSeo: "ROAS: o que é, como calcular e por que não é lucro",
    resumo:
      "ROAS (Return On Ad Spend) é a receita atribuída a um anúncio dividida pelo que você gastou nele. Mede retorno de mídia — não lucro, porque não desconta nenhum custo do produto.",
    secoes: [
      {
        h: "O que é ROAS",
        p: [
          "ROAS é a sigla de <strong>Return On Ad Spend</strong> (retorno sobre o investimento em anúncios). É a métrica que a Meta e o Google mostram no gerenciador para dizer quanto de receita cada real de mídia trouxe.",
          "A fórmula é simples: <strong>ROAS = receita atribuída ÷ gasto com anúncios</strong>. Um ROAS de 4 significa que cada R$ 1 investido gerou R$ 4 de receita atribuída.",
        ],
      },
      {
        h: "Como calcular o ROAS",
        p: [
          "Se você gastou R$ 2.000 em uma campanha e ela atribuiu R$ 8.000 em vendas, o ROAS é 8.000 ÷ 2.000 = <strong>4,0</strong>.",
          "O ponto cego está no <em>atribuída</em>: a plataforma conta a receita do jeito mais generoso possível (várias janelas de atribuição, view-through, último toque dela mesma). Por isso o ROAS informado tende a ser otimista.",
        ],
      },
      {
        h: "Por que ROAS não é lucro",
        p: [
          "ROAS olha só receita ÷ mídia. Não desconta o custo do produto (CMV), o frete, o imposto nem a própria margem. Uma campanha pode ter ROAS bonito e ainda assim dar prejuízo depois que esses custos entram na conta.",
          "Para saber se a campanha realmente sobra dinheiro, você precisa do <strong>ROAS de equilíbrio</strong> (o mínimo para não dar prejuízo) e do <strong>lucro das vendas</strong> (o que de fato fica no caixa).",
        ],
      },
    ],
    relacionados: ["roas-de-equilibrio", "margem-de-contribuicao", "lucro-das-vendas"],
    calculadora: {
      rotulo: "Calcular meu ROAS de equilíbrio",
      path: "/calculadora/roas-de-equilibrio",
    },
  },
  {
    slug: "roas-de-equilibrio",
    termo: "ROAS de equilíbrio",
    pergunta: "O que é o ROAS de equilíbrio (breakeven)?",
    tituloSeo: "ROAS de equilíbrio (breakeven): o que é e como calcular",
    resumo:
      "O ROAS de equilíbrio (ou breakeven) é o ROAS mínimo para uma campanha parar de consumir caixa. Acima dele, vale escalar; abaixo, o anúncio queima margem mesmo vendendo.",
    secoes: [
      {
        h: "O que é o ROAS de equilíbrio",
        p: [
          "O <strong>ROAS de equilíbrio</strong> — também chamado de breakeven ROAS — é o ponto em que a receita de uma campanha cobre exatamente todos os custos variáveis mais a própria mídia. Acima dele você lucra; abaixo, perde dinheiro a cada venda.",
          "É o número que transforma o ROAS de uma métrica de vaidade em uma régua de decisão: escalar, ajustar lance ou pausar.",
        ],
      },
      {
        h: "Como calcular o ROAS de equilíbrio",
        p: [
          "A fórmula é <strong>ROAS de equilíbrio = 1 ÷ margem de contribuição (em fração)</strong>, ou, de forma equivalente, receita ÷ margem de contribuição.",
          "Exemplo: se a sua margem de contribuição é 40% (você fica com R$ 0,40 de cada R$ 1,00 de venda depois de CMV, frete e imposto), o ROAS de equilíbrio é 1 ÷ 0,40 = <strong>2,5</strong>. Qualquer campanha rodando abaixo de 2,5 está dando prejuízo, por mais que o gerenciador mostre ROAS positivo.",
          "Quanto menor a sua margem, mais alto precisa ser o ROAS para a conta fechar — por isso lojas de margem apertada quebram escalando campanhas que pareciam boas.",
        ],
      },
      {
        h: "O que fazer com esse número",
        p: [
          "Compare o ROAS real de cada campanha com o seu ROAS de equilíbrio. Bem acima: escale. Em cima da linha: otimize criativo, público ou lance. Abaixo: pause antes que ela coma a margem do mês.",
        ],
      },
    ],
    relacionados: ["roas", "margem-de-contribuicao", "cmv"],
    calculadora: { rotulo: "Calcular agora", path: "/calculadora/roas-de-equilibrio" },
  },
  {
    slug: "margem-de-contribuicao",
    termo: "Margem de contribuição",
    pergunta: "O que é margem de contribuição no e-commerce?",
    tituloSeo: "Margem de contribuição no e-commerce: o que é e como calcular",
    resumo:
      "Margem de contribuição é a receita menos os custos variáveis (CMV, frete e imposto). É quanto cada venda deixa para pagar a mídia, os custos fixos e virar lucro — e a base do ROAS de equilíbrio.",
    secoes: [
      {
        h: "O que é margem de contribuição",
        p: [
          "A <strong>margem de contribuição</strong> é o que sobra de cada venda depois de descontar os custos que variam com ela: o CMV (custo do produto), o frete e os impostos. Ainda não é lucro — é o que <em>contribui</em> para pagar a mídia, os custos fixos e, no fim, formar o lucro.",
          "Pode ser expressa em reais (R$ por pedido) ou como percentual da receita (44% significa que R$ 0,44 de cada R$ 1,00 sobram após os custos variáveis).",
        ],
      },
      {
        h: "Como calcular a margem de contribuição",
        p: [
          "<strong>Margem de contribuição (R$) = preço de venda − CMV − frete − imposto</strong>. Para a versão percentual, divida esse valor pela receita.",
          "Exemplo: um produto vendido a R$ 150, com CMV de R$ 70, frete de R$ 15 e imposto de R$ 9, tem margem de contribuição de 150 − 70 − 15 − 9 = <strong>R$ 56</strong>, ou 37,3% da receita.",
        ],
      },
      {
        h: "Por que ela é tão importante",
        p: [
          "A margem de contribuição é a base do <strong>ROAS de equilíbrio</strong>: quanto menor a margem, mais alto precisa ser o ROAS para a campanha não dar prejuízo. Ignorá-la é o erro nº 1 de quem escala anúncios olhando só o ROAS do gerenciador.",
        ],
      },
    ],
    relacionados: ["roas-de-equilibrio", "cmv", "lucro-das-vendas"],
    calculadora: {
      rotulo: "Calcular a margem de um produto",
      path: "/calculadora/margem-de-contribuicao",
    },
  },
  {
    slug: "cmv",
    termo: "CMV",
    pergunta: "O que é CMV (Custo da Mercadoria Vendida)?",
    tituloSeo: "CMV (Custo da Mercadoria Vendida): o que é e como calcular",
    resumo:
      "CMV é o Custo da Mercadoria Vendida — quanto você pagou pelos produtos que vendeu no período. É o maior custo variável da maioria das lojas e o primeiro a entrar no cálculo do lucro real.",
    secoes: [
      {
        h: "O que é CMV",
        p: [
          "<strong>CMV</strong> significa <strong>Custo da Mercadoria Vendida</strong>: o valor que você efetivamente pagou pelos produtos que saíram em vendas num período. É um custo variável — cresce e diminui junto com o volume de vendas.",
          "Não confunda com estoque: o CMV considera só o que foi vendido, não tudo o que está no depósito.",
        ],
      },
      {
        h: "Como calcular o CMV",
        p: [
          "A fórmula clássica é <strong>CMV = estoque inicial + compras − estoque final</strong>. No nível do produto, é simplesmente o custo unitário de aquisição multiplicado pela quantidade vendida.",
          "Exemplo: você começou o mês com R$ 20.000 em estoque, comprou R$ 15.000 e terminou com R$ 18.000. O CMV do mês foi 20.000 + 15.000 − 18.000 = <strong>R$ 17.000</strong>.",
        ],
      },
      {
        h: "O papel do CMV no lucro real",
        p: [
          'O CMV costuma ser o maior dos custos variáveis e o primeiro a descontar da receita para chegar à margem de contribuição. Sem o CMV cadastrado por produto, qualquer ROAS ou "lucro" calculado é um chute — por isso ele é o ponto de partida de qualquer análise de lucro real.',
        ],
      },
    ],
    relacionados: ["margem-de-contribuicao", "lucro-das-vendas", "roas-de-equilibrio"],
  },
  {
    slug: "lucro-das-vendas",
    termo: "Lucro das vendas",
    pergunta: "O que é o lucro das vendas?",
    tituloSeo: "Lucro das vendas: o que é e como calcular o lucro real da loja",
    resumo:
      "Lucro das vendas é a receita menos os custos variáveis (CMV, frete, imposto) menos a mídia paga. É o número que paga o boleto — diferente do ROAS, que não desconta nada.",
    secoes: [
      {
        h: "O que é o lucro das vendas",
        p: [
          "O <strong>lucro das vendas</strong> é o que realmente sobra das vendas depois de descontar os custos variáveis (CMV, frete e imposto) e a mídia paga. É o número que paga o boleto no fim do mês.",
          "Importante: ele ainda <em>não</em> é o lucro líquido contábil — não desconta custos fixos (aluguel, salários, ferramentas) nem IRPJ/CSLL. É a camada que o lojista controla no dia a dia ajustando produtos e campanhas.",
        ],
      },
      {
        h: "Como calcular o lucro das vendas",
        p: [
          "<strong>Lucro das vendas = receita − CMV − frete − imposto − mídia paga</strong>.",
          "Exemplo: R$ 100.000 de receita, R$ 45.000 de CMV, R$ 8.000 de frete, R$ 6.000 de imposto e R$ 20.000 de mídia resultam em 100.000 − 45.000 − 8.000 − 6.000 − 20.000 = <strong>R$ 21.000</strong> de lucro das vendas.",
        ],
      },
      {
        h: "Por que ele revela campanhas no vermelho",
        p: [
          "Como o lucro das vendas desconta a mídia por último, ele expõe campanhas que vendem muito e mesmo assim destroem caixa — algo que o ROAS sozinho esconde. É o número que decide o que escalar e o que pausar.",
        ],
      },
    ],
    relacionados: ["roas", "margem-de-contribuicao", "cmv"],
  },
];

export function acharVerbete(slug: string | undefined): Verbete | undefined {
  return VERBETES.find((v) => v.slug === slug);
}

/** Tempo de leitura em minutos (~200 palavras/min, mínimo 1) a partir do resumo
 *  + seções, sem as tags de HTML simples. Compartilhado por verbetes e artigos. */
export function tempoLeituraSecoes(resumo: string, secoes: ReadonlyArray<SecaoConteudo>): number {
  const texto = [resumo, ...secoes.flatMap((s) => [s.h, ...s.p])].join(" ");
  const palavras = texto
    .replace(/<[^>]+>/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;
  return Math.max(1, Math.round(palavras / 200));
}
